import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";

/**
 * HeroLiveQuote — compact live-quote strip that sits inside the Hero.
 * Fetches real mid-market rates from /api/rates once, then recomputes the
 * receive amount instantly as the visitor types.
 */
const API = process.env.REACT_APP_BACKEND_URL;

// Fallback corridor set — used until /api/rates responds or if it errors.
const FALLBACK_CORRIDORS = [
  { from: "USD", to: "NGN", rate: 1585.0,  flag_from: "🇺🇸", flag_to: "🇳🇬", label: "Nigeria" },
  { from: "GBP", to: "KES", rate: 168.42,  flag_from: "🇬🇧", flag_to: "🇰🇪", label: "Kenya" },
  { from: "EUR", to: "GHS", rate: 14.51,   flag_from: "🇪🇺", flag_to: "🇬🇭", label: "Ghana" },
  { from: "CAD", to: "ZAR", rate: 13.24,   flag_from: "🇨🇦", flag_to: "🇿🇦", label: "S. Africa" },
  { from: "USD", to: "XOF", rate: 605.20,  flag_from: "🇺🇸", flag_to: "🇸🇳", label: "Senegal" },
  { from: "GBP", to: "NGN", rate: 2014.55, flag_from: "🇬🇧", flag_to: "🇳🇬", label: "Nigeria (GBP)" },
];

const CORRIDOR_LABELS = {
  NGN: "Nigeria", KES: "Kenya", GHS: "Ghana", ZAR: "S. Africa",
  XOF: "Senegal", UGX: "Uganda", MAD: "Morocco", EGP: "Egypt", TZS: "Tanzania",
};

const FEE_RATE = 0.009;
const CCY_SYMBOL = {
  USD: "$", GBP: "£", EUR: "€", CAD: "C$", AUD: "A$", CHF: "Fr",
  NGN: "₦", KES: "KSh", GHS: "GH₵", XOF: "CFA", ZAR: "R",
  UGX: "USh", MAD: "MAD", EGP: "E£", TZS: "TSh",
};

function fmt(n, decimals = 0) {
  if (!isFinite(n)) return "—";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export default function HeroLiveQuote() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const [amount, setAmount] = useState(500);
  const [idx, setIdx] = useState(0);
  const [corridors, setCorridors] = useState(FALLBACK_CORRIDORS);
  const [source, setSource] = useState("fallback");

  // Fetch live rates once on mount — silently keeps illustrative values on failure.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/rates`);
        if (!r.ok) return;
        const d = await r.json();
        if (!alive || !d?.pairs?.length) return;
        setCorridors(
          d.pairs.slice(0, 6).map((p) => ({
            from: p.from,
            to: p.to,
            rate: p.rate,
            flag_from: p.flag_from,
            flag_to: p.flag_to,
            label:
              (CORRIDOR_LABELS[p.to] || p.to) +
              (p.from !== "USD" && CORRIDOR_LABELS[p.to] ? ` (${p.from})` : ""),
          })),
        );
        setSource(d.source || "live");
      } catch (_) {
        // silent — keep fallback
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const corridor = corridors[idx] || corridors[0];
  const numeric = Number.isFinite(+amount) && +amount > 0 ? +amount : 0;

  const fee = useMemo(() => +(numeric * FEE_RATE).toFixed(2), [numeric]);
  const netSend = Math.max(0, numeric - fee);
  const receive = useMemo(
    () => netSend * (corridor?.rate || 0),
    [netSend, corridor],
  );

  const [tick, setTick] = useState(0);
  useEffect(() => {
    setTick((t) => t + 1);
  }, [numeric, idx, corridor?.rate]);

  const isLive = source !== "fallback";

  return (
    <motion.div
      id="send-widget"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.85, duration: 0.7 }}
      className="mt-10 max-w-xl rounded-3xl border border-forest/12 bg-white/85 backdrop-blur-md p-5 md:p-6 shadow-[0_18px_60px_-30px_rgba(74,29,150,0.35)]"
      data-testid="hero-live-quote"
    >
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">
          {t("liveQuote.eyebrow")} · {isLive ? t("liveQuote.liveMid") : "illustrative"}
        </div>
        <div className="flex items-center gap-1.5">
          {isLive && <span className="live-dot" aria-hidden />}
          <span className="text-[10px] tracking-[0.22em] uppercase text-forest/70 font-semibold">
            {isLive ? "live" : "sample"} · 0.9%
          </span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 sm:items-center">
        <label className="block">
          <div className="text-[10px] tracking-[0.22em] uppercase text-ink/50 font-semibold">
            {t("liveQuote.youSend")}
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="font-display text-2xl sm:text-3xl md:text-4xl text-forest tracking-tightest">
              {CCY_SYMBOL[corridor.from] || corridor.from}
            </span>
            <input
              type="number"
              min="1"
              step="1"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full min-w-0 bg-transparent font-display text-2xl sm:text-3xl md:text-4xl text-forest tracking-tightest focus:outline-none placeholder:text-forest/25"
              placeholder="500"
              aria-label={t("liveQuote.youSend")}
              data-testid="hero-quote-amount"
            />
          </div>
        </label>

        <label className="block">
          <div className="text-[10px] tracking-[0.22em] uppercase text-ink/50 font-semibold sm:text-right">
            {t("liveQuote.corridor")}
          </div>
          <select
            className="mt-1 w-full sm:w-auto rounded-xl border border-forest/15 bg-canvas px-3 py-2 text-sm text-forest font-mono focus:outline-none focus:border-forest/40 cursor-pointer"
            value={idx}
            onChange={(e) => setIdx(+e.target.value)}
            aria-label={t("liveQuote.corridor")}
            data-testid="hero-quote-corridor"
          >
            {corridors.map((c, i) => (
              <option key={c.from + c.to} value={i}>
                {`${c.flag_from} → ${c.flag_to} · ${c.label}`}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-5 rounded-2xl bg-forest text-canvas p-4 md:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 overflow-hidden">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.24em] uppercase text-lime font-semibold">
            {t("liveQuote.receive")}
          </div>
          <motion.div
            key={tick}
            initial={{ opacity: 0.4, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
            className="mt-1 font-display text-2xl sm:text-3xl md:text-[38px] tracking-tightest leading-none break-words"
            data-testid="hero-quote-receive"
          >
            {CCY_SYMBOL[corridor.to] || corridor.to} {fmt(receive, 0)}
          </motion.div>
          <div className="mt-1.5 text-[11px] text-canvas/60 tracking-wide">
            {`1 ${corridor.from} = ${fmt(corridor.rate, 2)} ${corridor.to} · ${t("liveQuote.fee")} ${CCY_SYMBOL[corridor.from] || corridor.from} ${fmt(fee, 2)}`}
          </div>
        </div>
        <Link
          to={prefix(`/waitlist?send=${numeric}&from=${corridor.from}&to=${corridor.to}&receive=${Math.round(receive)}&source=hero-quote`)}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-lime text-ink px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-lime/90 transition-colors"
          data-testid="hero-quote-send"
        >
          {t("liveQuote.getRate")} <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
        </Link>
      </div>
    </motion.div>
  );
}
